/* Harber London: product page (main product section, recommendations, reviews CTA).
 * Custom elements: <product-info>, <variant-picker>, <product-gallery>, <sticky-atc>, <product-recommendations>.
 * Depends on window.Theme (assets/theme.js) for cart, money formatting and debounce. */
(() => {
  if (window.__harberProductJs) return;
  window.__harberProductJs = true;

  const T = (window.Theme = window.Theme || {});
  /* Recently viewed (home "Recently viewed" tab, assets/product-tabs.js): the last 12 product handles, newest first */
  try {
    const m = /\/products\/([^/?#]+)/.exec(location.pathname);
    if (m) {
      const h = decodeURIComponent(m[1]);
      const list = JSON.parse(localStorage.getItem('harber:recent') || '[]').filter((x) => x !== h);
      localStorage.setItem('harber:recent', JSON.stringify([h, ...list].slice(0, 12)));
    }
  } catch { /* private mode */ }
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const debounce = (fn, ms = 80) => (T.debounce ? T.debounce(fn, ms) : fn);
  const money = (cents) => (T.formatMoney ? T.formatMoney(cents) : (cents / 100).toFixed(2));
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  /* Option names that already read as an instruction ("Choose the amount", "Elija el importe") */
  const IMPERATIVE = /(choose|select|pick|elige|elija|escoge|selecciona|wähle|wählen|choisi|sélection|scegli|seleziona|kies|selecteer|escolh)/i;

  /* Handle-ize like Shopify's `handle` filter (accents stripped, non-alphanumerics to "-"). */
  const handleize = (s) =>
    String(s ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  T.handleize = handleize;

  /* Multilingual colour synonyms (from the previous theme's product-form.js). Image alt text is
   * written in English, option values arrive translated: every name in a row matches the others. */
  const COLOR_TRANSLATIONS = {
    'Tan': { es: 'Tan', fr: 'Tan', de: 'Tan', it: 'Abbronzatura' },
    'Black': { es: 'Negro', fr: 'Noir', de: 'Schwarz', it: 'Nero' },
    'Deep Brown': { es: 'Marrón oscuro', fr: 'Marron profond', de: 'Dunkelbraun', it: 'Marrone scuro' },
    'Navy': { es: 'Azul', fr: 'Marine', de: 'Marineblau', it: 'marina' },
    'Grey': { es: 'Gris', fr: 'Gris', de: 'Grau', it: 'Grigio' },
    'Burgundy': { es: 'Borgoña', fr: 'Bourgogne', de: 'Burgunder', it: 'Borgogna' },
    'Stone': { es: 'Piedra', fr: 'Pierre', de: 'Stein', it: 'pietra' },
    'Grey/Tan': { es: 'Gris / Tostado', fr: 'Gris / Tan', de: 'Grau / Tan', it: 'Grey / Tan' },
    'Tan/Black': { es: 'Bronceado / negro', fr: 'Tan / Noir', de: 'Hellbraun / Schwarz', it: 'Tan / nero' },
    'Tan/Tan': { es: 'Tan / Tan', fr: 'Tan / Tan', de: 'Hellbraun/hellbraun', it: 'Abbronzatura/Abbronzatura' },
    'Tan/Grey': { es: 'Tan / Grey', fr: 'Tan / Gris', de: 'Hellbraun/Grau', it: 'Marrone chiaro/grigio' },
    'Black/Tan': { es: 'Negro / Canela', fr: 'Noir / Tan', de: 'Schwarz / Hellbraun', it: 'Nero / Tan' },
    'Black/Black': { es: 'Negro/Negro', fr: 'Noir / noir', de: 'Schwarz Schwarz', it: 'Nero / Nero' },
    'Black/Grey': { es: 'Negro / Gris', fr: 'Noir / Gris', de: 'Schwarz / Grau', it: 'Nero / Grigio' },
    'Tan/Black Felt': { es: 'Tan / fieltro negro', fr: 'Feutre beige / noir', de: 'Hellbrauner/schwarzer Filz', it: 'Feltro marrone chiaro/nero' },
    'Tan/Dark Felt': { es: 'Tan / fieltro oscuro', fr: 'Feutre beige / foncé', de: 'Hellbrauner/dunkler Filz', it: 'Feltro marrone chiaro/scuro' },
    'Tan/Light Felt': { es: 'Tan / fieltro ligero', fr: 'Feutre beige / clair', de: 'Hellbraun/leichter Filz', it: 'Feltro marrone chiaro/chiaro' },
    'Black/Black Felt': { es: 'Fieltro negro / negro', fr: 'Feutre noir / noir', de: 'Schwarz/Schwarzer Filz', it: 'Feltro nero/nero' },
    'Black/Dark Felt': { es: 'Fieltro negro / oscuro', fr: 'Feutre noir / foncé', de: 'Schwarz/dunkler Filz', it: 'Feltro nero/scuro' },
    'Navy/Light Felt': { es: 'Azul marino / fieltro ligero', fr: 'Marine / feutre clair', de: 'Marine/heller Filz', it: 'Navy/feltro chiaro' },
    'Grey Microfibre': { es: 'Microfibra Gris', fr: 'Microfibre grise', de: 'Graue Mikrofaser', it: 'Microfibra grigia' },
    'Cognac': { es: 'Coñac', fr: 'Cognac', de: 'Cognac', it: 'Cognac' },
    'Olive': { es: 'Verde Oliva', fr: 'Olive', de: 'Olive', it: 'Oliva' },
    'Mocha': { es: 'Mocha', fr: 'Mocha', de: 'Mocha', it: 'Mocha' },
    'Camel': { es: 'Camel', fr: 'Camel', de: 'Camel', it: 'Cammello' },
    'Chestnut': { es: 'Chestnut', fr: 'chataîgne', de: 'Kastanie', it: 'Castagna' },
    'Caoba': { es: 'Caoba', fr: 'Caoba', de: 'Caoba', it: 'Mogano' },
    'Amber': { es: 'Ámbar', fr: 'Ambre', de: 'Bernstein', it: 'Ambra' },
    'Army Green': { es: 'Verde militar', fr: 'Vert armée', de: 'Armeegrün', it: 'Verde militare' }
  };
  const COLOUR_ROWS = Object.entries(COLOR_TRANSLATIONS).map(([en, tr]) => [en, ...Object.values(tr)].map(handleize));
  const colourTerms = (value) => {
    const h = handleize(value);
    const terms = new Set([h]);
    COLOUR_ROWS.forEach((row) => { if (row.includes(h)) row.forEach((t) => terms.add(t)); });
    terms.delete('');
    return [...terms];
  };
  const altHas = (alt, term) => !!term && `-${alt || ''}-`.includes(`-${term}-`);

  const scripts = {};
  const loadScript = (src) =>
    (scripts[src] = scripts[src] || new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.defer = true;
      s.onload = resolve;
      s.onerror = (e) => {
        /* Forget the failure so a later call can try again. */
        delete scripts[src];
        s.remove();
        reject(e);
      };
      document.head.appendChild(s);
    }));

  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* ------------------------------------------------------------- Gallery */
  /* Mobile: horizontal swipe track with dots. Desktop (990px+): every visible slide stacked
   * vertically, with a thin progress indicator (left edge) following the image in view. */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.dots = this.querySelector('[data-dots]');
      this.count = this.querySelector('[data-count]');
      this.progress = this.querySelector('[data-progress]');
      this.lightbox = this.querySelector('[data-lightbox]');
      this.mq = matchMedia('(min-width: 990px)');

      this.track.addEventListener('scroll', debounce(() => this.sync(), 60), { passive: true });
      let raf = 0;
      addEventListener('scroll', () => {
        if (!this.stacked() || raf) return;
        raf = requestAnimationFrame(() => { raf = 0; this.sync(); });
      }, { passive: true });
      this.mq.addEventListener?.('change', () => { this.buildDots(); this.sync(); });
      this.track.addEventListener('click', (e) => {
        const z = e.target.closest('[data-zoom]');
        if (z) { this.zoomByPointer = e.detail > 0; this.openZoom(z.dataset.zoom); }
      });
      this.dots?.addEventListener('click', (e) => {
        const b = e.target.closest('button[data-index]');
        if (b) this.scrollToIndex(Number(b.dataset.index));
      });
      this.track.addEventListener('keydown', (e) => {
        if (this.stacked()) return;
        if (e.key === 'ArrowRight') this.step(1);
        if (e.key === 'ArrowLeft') this.step(-1);
      });
      this.querySelectorAll('[data-model] [data-play]').forEach((b) =>
        b.addEventListener('click', () => window.Shopify?.loadFeatures?.([{ name: 'model-viewer-ui', version: '1.0', onLoad: () => {} }]))
      );
      this.setupLightbox();
      new ResizeObserver(debounce(() => this.sync(), 100)).observe(this.track);
      this.buildDots();
      this.sync();
    }

    stacked() { return this.mq.matches; }

    visible() { return this.slides.filter((s) => !s.hidden); }

    index() {
      const vis = this.visible();
      if (this.stacked()) {
        const mid = innerHeight / 2;
        let best = 0;
        let bestD = Infinity;
        vis.forEach((s, i) => {
          const r = s.getBoundingClientRect();
          const d = r.top <= mid && r.bottom >= mid ? 0 : Math.min(Math.abs(r.top - mid), Math.abs(r.bottom - mid));
          if (d < bestD) { bestD = d; best = i; }
        });
        return best;
      }
      const w = this.track.clientWidth || 1;
      return Math.max(0, Math.min(vis.length - 1, Math.round(this.track.scrollLeft / w)));
    }

    buildDots() {
      const n = this.visible().length;
      if (this.dots) {
        const label = this.dots.dataset.label || '[index]';
        this.dots.replaceChildren(...Array.from({ length: n }, (_, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.dataset.index = String(i);
          b.setAttribute('aria-label', label.replace('[index]', String(i + 1)));
          b.append(document.createElement('span'));
          return b;
        }));
        this.dots.hidden = n < 2;
      }
      if (this.count) this.count.hidden = n < 2;
      if (this.progress) {
        this.progress.style.setProperty('--n', String(Math.max(1, n)));
        this.progress.hidden = n < 2;
      }
    }

    sync() {
      const vis = this.visible();
      const i = this.index();
      const cur = vis[i];
      this.dots?.querySelectorAll('button').forEach((b, n) => {
        b.querySelector('span')?.classList.toggle('is-active', n === i);
        if (n === i) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
      });
      if (this.count) this.count.textContent = `${Math.min(i + 1, vis.length)} / ${vis.length}`;
      this.progress?.style.setProperty('--i', String(i));
      if (this.stacked()) return; /* stacked: every slide is on screen at some point, leave videos alone */
      this.slides.forEach((s) => s.querySelectorAll('video').forEach((v) => {
        if (s !== cur) v.pause();
        else if (v.hasAttribute('autoplay') && v.muted) v.play?.().catch(() => {});
      }));
    }

    scrollToIndex(i, smooth = true) {
      if (this.stacked()) return; /* never move the page when the gallery is a vertical stack */
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: smooth && !reduced ? 'smooth' : 'auto' });
    }

    goTo(id, smooth = true) {
      const i = this.visible().findIndex((s) => s.dataset.mediaId === String(id));
      if (i > -1) this.scrollToIndex(i, smooth);
    }

    step(d) {
      const n = this.visible().length;
      this.scrollToIndex(Math.max(0, Math.min(n - 1, this.index() + d)));
    }

    /* Keep only the images whose alt contains the colour (and, when possible, a word from the other
     * selected options). Falls back to every image. Videos/3D always stay. On load (keepLead) the
     * server-rendered lead slide (the LCP image) also stays, so the filter never hides it. */
    filter({ colour, others = [], mediaId = null, keepLead = false }) {
      const imgs = this.slides.filter((s) => !s.hasAttribute('data-always'));
      let keep = null;
      if (colour) {
        const terms = colourTerms(colour);
        const words = others.flatMap((v) => String(v).split(/\s+/)).filter((w) => w.length > 2).map(handleize).filter(Boolean);
        const byColour = (s) => terms.some((t) => altHas(s.dataset.alt, t));
        let list = words.length ? imgs.filter((s) => byColour(s) && words.some((w) => altHas(s.dataset.alt, w))) : [];
        if (!list.length) list = imgs.filter(byColour);
        if (list.length) keep = new Set(list.map((s) => s.dataset.mediaId));
      }
      const lead = keepLead ? this.track.querySelector('[data-lead]')?.dataset.mediaId : null;
      if (keep && lead) keep.add(lead);
      const show = (el) => !keep || el.hasAttribute('data-always') || keep.has(el.dataset.mediaId);
      this.slides.forEach((s) => (s.hidden = !show(s)));
      this.lightbox?.querySelectorAll('.pdp-lightbox__slide').forEach((s) => (s.hidden = !show(s)));
      this.buildDots();
      const vis = this.visible();
      const target = mediaId ? vis.findIndex((s) => s.dataset.mediaId === String(mediaId)) : -1;
      this.scrollToIndex(Math.max(0, target), false);
      this.sync();
    }

    setupLightbox() {
      const lb = this.lightbox;
      if (!lb) return;
      this.lbTrack = lb.querySelector('[data-lightbox-track]');
      this.lbIndex = lb.querySelector('[data-lb-index]');
      this.lbCount = lb.querySelector('[data-lb-count]');
      this.lbPrev = lb.querySelector('[data-lb-prev]');
      this.lbNext = lb.querySelector('[data-lb-next]');
      lb.querySelector('[data-lightbox-close]')?.addEventListener('click', () => window.Theme?.closeDialog ? window.Theme.closeDialog(lb) : lb.close());
      lb.beforeClose = () => this.flyOut();
      lb.addEventListener('close', () => {
        document.documentElement.classList.remove('is-locked');
        this.lbSlides().forEach((sl) => this.resetZoom(sl.querySelector('[data-pane]'), false));
        lb.classList.remove('is-zoomed', 'is-dragging');
        lb.style.removeProperty('--lb-bg');
        // Give focus back to the photo that opened the zoom, without a focus ring when it was clicked.
        const opener = this.zoomOpener;
        if (opener) {
          if (this.zoomByPointer) { opener.classList.add('no-ring'); opener.addEventListener('blur', () => opener.classList.remove('no-ring'), { once: true }); }
          opener.focus({ preventScroll: true, focusVisible: !this.zoomByPointer });
        }
      });
      this.lbPrev?.addEventListener('click', () => this.lbGo(-1));
      this.lbNext?.addEventListener('click', () => this.lbGo(1));
      lb.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.lbGo(e.key === 'ArrowRight' ? 1 : -1); }
      });
      let raf = 0;
      this.lbTrack.addEventListener('scroll', () => {
        if (raf) return;
        raf = requestAnimationFrame(() => { raf = 0; this.lbSync(); });
      }, { passive: true });

      /* One pointer handler per pane: tap = zoom in/out (or close when tapping beside the photo),
       * drag while zoomed = pan, vertical swipe while not zoomed = close. */
      let drag = null;
      lb.addEventListener('pointerdown', (e) => {
        const pane = e.target.closest('[data-pane]');
        if (!pane || e.button > 0) return;
        drag = { pane, img: pane.querySelector('img'), x: e.clientX, y: e.clientY, tx: pane._tx || 0, ty: pane._ty || 0, moved: false, vertical: false, id: e.pointerId };
        if (pane._s > 1) { pane.setPointerCapture(e.pointerId); e.preventDefault(); }
      });
      lb.addEventListener('pointermove', (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const dx = e.clientX - drag.x;
        const dy = e.clientY - drag.y;
        if (!drag.moved && Math.hypot(dx, dy) < 6) return;
        drag.moved = true;
        const { pane, img } = drag;
        if (pane._s > 1) {
          pane.classList.add('is-dragging');
          img.classList.remove('is-animating');
          this.applyZoom(pane, pane._s, drag.tx + dx, drag.ty + dy);
        } else if (e.pointerType !== 'mouse' && (drag.vertical || Math.abs(dy) > Math.abs(dx))) {
          drag.vertical = true;
          lb.classList.add('is-dragging');
          img.classList.remove('is-animating');
          img.style.transform = `translate3d(0, ${dy}px, 0)`;
          lb.style.setProperty('--lb-bg', String(Math.max(0.2, 1 - Math.abs(dy) / 400)));
        }
      });
      const end = (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const { pane, img, moved, vertical } = drag;
        const dy = e.clientY - drag.y;
        drag = null;
        pane.classList.remove('is-dragging');
        if (vertical) {
          lb.classList.remove('is-dragging');
          if (e.type === 'pointerup' && Math.abs(dy) > 110) { window.Theme?.closeDialog ? window.Theme.closeDialog(lb) : lb.close(); return; }
          lb.style.removeProperty('--lb-bg');
          img.classList.add('is-animating');
          img.style.transform = '';
          return;
        }
        if (moved || e.type !== 'pointerup') return;
        if (pane._s > 1) { this.resetZoom(pane, true); return; }
        const c = this.contentRect(img);
        const inside = e.clientX >= c.left && e.clientX <= c.left + c.width && e.clientY >= c.top && e.clientY <= c.top + c.height;
        if (!inside) { window.Theme?.closeDialog ? window.Theme.closeDialog(lb) : lb.close(); return; }
        this.zoomAt(pane, e.clientX, e.clientY);
      };
      lb.addEventListener('pointerup', end);
      lb.addEventListener('pointercancel', end);
    }

    /* The zoom slides arrive as a <template> and are built on the first open; they take the gallery's current
     * colour filter (same media ids hidden). */
    hydrateLightbox() {
      const tpl = this.lbTrack?.querySelector('template[data-lb-slides]');
      if (!tpl) return;
      const hiddenIds = new Set(this.slides.filter((s) => s.hidden).map((s) => s.dataset.mediaId));
      tpl.replaceWith(tpl.content);
      this.lightbox.querySelectorAll('.pdp-lightbox__slide').forEach((s) => (s.hidden = hiddenIds.has(s.dataset.mediaId)));
    }

    lbSlides() { return [...this.lightbox.querySelectorAll('.pdp-lightbox__slide:not([hidden])')]; }

    lbCurrent() {
      const w = this.lbTrack.clientWidth || 1;
      return Math.round(this.lbTrack.scrollLeft / w);
    }

    lbSync() {
      const slides = this.lbSlides();
      const i = Math.min(slides.length - 1, Math.max(0, this.lbCurrent()));
      // Only write on change: the counter is aria-live, so rewriting it every frame re-announces it.
      if (this.lbIndex && this.lbIndex.textContent !== String(i + 1)) this.lbIndex.textContent = String(i + 1);
      if (this.lbCount && this.lbCount.textContent !== String(slides.length)) this.lbCount.textContent = String(slides.length);
      if (this.lbPrev) this.lbPrev.disabled = i <= 0;
      if (this.lbNext) this.lbNext.disabled = i >= slides.length - 1;
      if (i !== this.lbLast) {
        this.lbLast = i;
        slides.forEach((sl, j) => { if (j !== i) this.resetZoom(sl.querySelector('[data-pane]'), false); });
      }
    }

    lbGo(dir) {
      const pane = this.lbSlides()[this.lbCurrent()]?.querySelector('[data-pane]');
      if (pane) this.resetZoom(pane, false);
      this.lbTrack.scrollBy({ left: dir * this.lbTrack.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
    }

    /* Where the photo actually sits inside its object-fit: contain box. */
    contentRect(img) {
      // Measured from the pane (never transformed), so it is right mid-zoom or mid-flight too.
      const pane = img.closest('[data-pane]');
      const r = pane.getBoundingClientRect();
      const cs = getComputedStyle(pane);
      const pl = parseFloat(cs.paddingLeft);
      const pt = parseFloat(cs.paddingTop);
      const box = { left: r.left + pl, top: r.top + pt, width: r.width - pl - parseFloat(cs.paddingRight), height: r.height - pt - parseFloat(cs.paddingBottom) };
      const nw = img.naturalWidth || Number(img.getAttribute('width')) || box.width;
      const nh = img.naturalHeight || Number(img.getAttribute('height')) || box.height;
      const ratio = nw / nh;
      let width = box.width;
      let height = box.width / ratio;
      if (height > box.height) { height = box.height; width = box.height * ratio; }
      return { box, left: box.left + (box.width - width) / 2, top: box.top + (box.height - height) / 2, width, height };
    }

    applyZoom(pane, s, tx, ty) {
      const img = pane.querySelector('img');
      const c = this.contentRect(img);
      const vw = innerWidth;
      const vh = innerHeight;
      // Keep the enlarged photo covering the screen (or centred when it is smaller than it).
      const offX = c.left - c.box.left;
      const offY = c.top - c.box.top;
      const clamp = (t, off, size, view, start) => {
        const left = start + t + s * off;
        const w = s * size;
        const want = w <= view ? (view - w) / 2 : Math.min(0, Math.max(view - w, left));
        return want - start - s * off;
      };
      tx = clamp(tx, offX, c.width, vw, c.box.left);
      ty = clamp(ty, offY, c.height, vh, c.box.top);
      pane._s = s; pane._tx = tx; pane._ty = ty;
      img.style.transform = s === 1 ? '' : `translate3d(${tx}px, ${ty}px, 0) scale(${s})`;
    }

    zoomAt(pane, x, y) {
      const img = pane.querySelector('img');
      const c = this.contentRect(img);
      const nw = img.naturalWidth || c.width * 2.5;
      const s = Math.max(2, Math.min(4, nw / c.width));
      img.classList.toggle('is-animating', !reduced);
      pane.classList.add('is-zoomed');
      this.lightbox.classList.add('is-zoomed');
      this.applyZoom(pane, s, (x - c.box.left) * (1 - s), (y - c.box.top) * (1 - s));
    }

    resetZoom(pane, animate) {
      if (!pane) return;
      const img = pane.querySelector('img');
      pane._s = 1; pane._tx = 0; pane._ty = 0;
      pane.classList.remove('is-zoomed', 'is-dragging');
      if (img) { img.classList.toggle('is-animating', animate && !reduced); img.style.transform = ''; }
      if (!this.lightbox.querySelector('.pdp-lightbox__pane.is-zoomed')) this.lightbox.classList.remove('is-zoomed');
    }

    /* Transform that lays the lightbox photo exactly over a gallery image. */
    flightTransform(img, from) {
      const c = this.contentRect(img);
      const k = Math.max(from.width / c.width, from.height / c.height);
      const cx = from.left + from.width / 2;
      const cy = from.top + from.height / 2;
      const tx = cx - c.box.left - k * (c.left + c.width / 2 - c.box.left);
      const ty = cy - c.box.top - k * (c.top + c.height / 2 - c.box.top);
      return `translate3d(${tx}px, ${ty}px, 0) scale(${k})`;
    }

    sourceRect(id) {
      const src = this.track.querySelector(`[data-zoom="${id}"] img`);
      if (!src) return null;
      const r = src.getBoundingClientRect();
      const visible = r.width > 0 && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth;
      return visible ? r : null;
    }

    openZoom(id) {
      const lb = this.lightbox;
      if (!lb || lb.open) return;
      this.zoomOpener = document.activeElement;
      const from = reduced ? null : this.sourceRect(id);
      this.hydrateLightbox();
      lb.showModal();
      document.documentElement.classList.add('is-locked');
      const slides = this.lbSlides();
      const i = Math.max(0, slides.findIndex((s) => s.dataset.mediaId === String(id)));
      this.lbTrack.style.scrollBehavior = 'auto';
      this.lbTrack.scrollLeft = i * this.lbTrack.clientWidth;
      this.lbTrack.style.scrollBehavior = '';
      this.lbLast = -1;
      this.lbSync();
      const img = slides[i]?.querySelector('img');
      if (!from || !img) return;
      img.classList.remove('is-animating');
      img.style.transform = this.flightTransform(img, from);
      img.getBoundingClientRect(); // commit the start position
      requestAnimationFrame(() => {
        img.classList.add('is-animating');
        img.style.transform = '';
      });
    }

    /* Closing: the photo flies back into its gallery image while the canvas fades. Returns true when
     * it handles the close itself (Theme.closeDialog then stays out of the way). */
    flyOut() {
      const lb = this.lightbox;
      if (reduced) return false;
      const slide = this.lbSlides()[this.lbCurrent()];
      const img = slide?.querySelector('img');
      const pane = slide?.querySelector('[data-pane]');
      if (pane && pane._s > 1) { pane._s = 1; pane.classList.remove('is-zoomed'); }
      const to = slide && this.sourceRect(slide.dataset.mediaId);
      lb.classList.add('is-closing');
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        lb.classList.remove('is-closing');
        this.lbTrack.classList.remove('is-flying');
        if (img) { img.classList.remove('is-animating'); img.style.transform = ''; }
        lb.close();
      };
      if (to && img) {
        this.lbTrack.classList.add('is-flying');
        const current = img.style.transform;
        img.style.transform = '';
        const target = this.flightTransform(img, to);
        img.classList.remove('is-animating');
        img.style.transform = current;
        img.getBoundingClientRect();
        img.classList.add('is-animating');
        img.style.transform = target;
        img.addEventListener('transitionend', finish, { once: true });
      }
      setTimeout(finish, 450);
      return true;
    }
  }
  define('product-gallery', ProductGallery);

  /* ------------------------------------------------------ Variant picker */
  class VariantPicker extends HTMLElement {
    /* Option groups live in the picker and, for the colour, further down the panel: read them all
     * from the product root, in option order. */
    groups() {
      const root = this.closest('product-info') || this;
      return [...root.querySelectorAll('.pdp-option[data-option-index]')].sort((a, b) => a.dataset.optionIndex - b.dataset.optionIndex);
    }

    read() {
      return this.groups().map((g) => {
        const sel = g.querySelector('[data-option-select]');
        if (sel) return sel.value;
        return g.querySelector('[data-option-radio]:checked')?.value ?? '';
      });
    }

    set(values) {
      this.groups().forEach((g, i) => {
        const v = values[i];
        if (v == null || v === '') return;
        const sel = g.querySelector('[data-option-select]');
        if (sel) { sel.value = v; return; }
        g.querySelectorAll('[data-option-radio]').forEach((r) => (r.checked = r.value === v));
      });
    }

    /* Mark values that cannot be bought with the other current selections. */
    mark(variants, values, soldOutSuffix) {
      this.groups().forEach((g, idx) => {
        const ok = (value) => variants.some((v) => v.available && v.options[idx] === value && v.options.every((o, j) => j === idx || !values[j] || o === values[j]));
        g.querySelectorAll('[data-option-radio]').forEach((r) => r.nextElementSibling?.classList.toggle('is-unavailable', !ok(r.value)));
        g.querySelectorAll('[data-option-select] option').forEach((o) => {
          if (!o.value) return;
          o.textContent = o.value + (ok(o.value) ? '' : soldOutSuffix);
        });
      });
    }
  }
  define('variant-picker', VariantPicker);

  /* ---------------------------------------------------------- Sticky bar */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.info = this.closest('product-info');
      const target = this.info?.querySelector('[data-atc]');
      if (!target || !('IntersectionObserver' in window)) return;
      this.hidden = false;
      this.btn = this.querySelector('[data-sticky-btn]');
      this.btn?.addEventListener('click', () => this.info.stickyBuy());
      this.buildOptions();
      /* Shown once the main button has scrolled away (on desktop also before it comes into view), hidden again when
       * the footer comes into view. */
      let pastButton = false;
      const desk = matchMedia('(min-width: 990px)');
      let footerIn = false;
      const footer = document.querySelector('.site-footer');
      const apply = () => {
        const visible = pastButton && !footerIn;
        this.classList.toggle('is-visible', visible);
        /* Lets floating widgets (Klaviyo teaser, app banner) sit above the bar. */
        document.documentElement.classList.toggle('has-sticky-atc', visible);
      };
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          /* Desktop: also while the main button is still below the fold (short laptop screens), so "Add to bag" is
           * always on screen; phones keep the bar for after the button (their first screen is the gallery) */
          if (e.target === target) pastButton = !e.isIntersecting && (e.boundingClientRect.top < 0 || (desk.matches && e.boundingClientRect.top >= innerHeight));
          else footerIn = e.isIntersecting;
        });
        apply();
      });
      this.io.observe(target);
      if (footer) this.io.observe(footer);
      /* WebKit runs the first observer callback before it restores the scroll position (Back, reload), and a jump past
       * the button fires no later callback: re-measure on the first scroll and on pageshow. */
      const measure = () => {
        const r = target.getBoundingClientRect();
        pastButton = r.bottom < 0 || (desk.matches && r.top >= innerHeight);
        if (footer) { const f = footer.getBoundingClientRect(); footerIn = f.top < innerHeight && f.bottom > 0; }
        apply();
      };
      addEventListener('scroll', measure, { passive: true, once: true });
      addEventListener('pageshow', () => requestAnimationFrame(measure));
    }

    disconnectedCallback() {
      this.io?.disconnect();
      document.documentElement.classList.remove('has-sticky-atc');
    }

    /* One <select> per product option, mirroring the main picker (radios or selects). Changing it
     * sets the main input and fires its change event, so ProductInfo handles it as usual. */
    buildOptions() {
      this.optsEl = this.querySelector('[data-sticky-options]');
      const picker = this.info.querySelector('variant-picker');
      if (!this.optsEl || !picker) return;
      this.groups = picker.groups();
      const choose = this.dataset.choose || '[option]';
      this.selects = this.groups.map((g) => {
        const name = g.dataset.optionName || '';
        const main = g.querySelector('[data-option-select]');
        const values = main
          ? [...main.options].filter((o) => o.value).map((o) => o.value)
          : [...g.querySelectorAll('[data-option-radio]')].map((r) => r.value);
        const sel = document.createElement('select');
        sel.className = 'field pdp-sticky__select';
        sel.setAttribute('aria-label', name);
        const ph = new Option(choose.replace('[option]', name), '');
        ph.disabled = true;
        sel.append(ph, ...values.map((v) => new Option(v, v)));
        sel.addEventListener('change', () => this.choose(g, sel.value));
        this.optsEl.append(sel);
        return sel;
      });
      this.sync();
    }

    choose(group, value) {
      const main = group.querySelector('[data-option-select]');
      let input = main;
      if (main) main.value = value;
      else {
        input = [...group.querySelectorAll('[data-option-radio]')].find((r) => r.value === value);
        if (input) input.checked = true;
      }
      input?.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Current values and sold-out marks from the main picker. */
    sync() {
      if (!this.selects) return;
      const suffix = this.info.s?.soldOutSuffix || '';
      this.selects.forEach((sel, i) => {
        const g = this.groups[i];
        const main = g.querySelector('[data-option-select]');
        const current = main ? main.value : g.querySelector('[data-option-radio]:checked')?.value || '';
        sel.value = current;
        sel.classList.toggle('is-missing', !current);
        [...sel.options].forEach((o) => {
          if (!o.value) return;
          let off;
          if (main) off = [...main.options].find((m) => m.value === o.value)?.textContent !== o.value;
          else off = [...g.querySelectorAll('[data-option-radio]')].find((r) => r.value === o.value)?.nextElementSibling?.classList.contains('is-unavailable');
          o.textContent = o.value + (off ? suffix : '');
        });
        window.Theme?.selectFace?.refresh(sel);
      });
    }

    /* Mirrors the main button while adding: label + aria-busy. */
    busy(label, on) {
      if (!this.btn) return;
      if (label) this.btn.textContent = label;
      if (on) this.btn.setAttribute('aria-busy', 'true');
      else this.btn.removeAttribute('aria-busy');
    }

    render({ variant, label, disabled, showVariant, priceText }) {
      this.sync();
      const price = this.querySelector('[data-sticky-price]');
      if (price) price.textContent = priceText || money(variant.price);
      const vt = this.querySelector('[data-sticky-variant]');
      if (vt) vt.textContent = showVariant ? ` · ${variant.title}` : '';
      const img = this.querySelector('[data-sticky-img]');
      if (img && variant.image) img.src = variant.image.replace(/width=\d+/, 'width=160');
      const btn = this.btn;
      if (btn) {
        btn.textContent = label;
        btn.classList.toggle('btn--gold', !!variant.preorder && variant.available && !disabled);
        btn.disabled = disabled;
      }
    }
  }
  define('sticky-atc', StickyAtc);

  /* --------------------------------------------------------- Product info */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      if (!json) return;
      try { this.data = JSON.parse(json.textContent); } catch { return; }
      this.s = this.data.strings || {};
      this.form = this.querySelector('[data-product-form]');
      this.input = this.form?.querySelector('[data-variant-input]');
      this.picker = this.querySelector('variant-picker');
      this.gallery = this.querySelector('product-gallery');
      this.sticky = this.querySelector('sticky-atc');
      this.atc = this.querySelector('[data-atc]');
      /* The id input is empty while a choice is needed (so nothing is added before this runs): data-default holds the
       * variant the page opened on */
      const start = this.input?.value || this.input?.dataset.default;
      this.variant = this.data.variants.find((v) => String(v.id) === start) || this.data.variants[0];
      this.atc?.removeAttribute('data-wait-js');
      this.chosen = !this.data.requireChoice;

      this.addEventListener('change', (e) => {
        if (this.picker && e.target.closest('.pdp-option')) return this.onOptionChange(e);
        if (e.target.matches?.('select[data-stl-variant]')) {
          const pr = e.target.closest('[data-stl]')?.querySelector('.pdp-stl__price');
          const opt = e.target.selectedOptions?.[0];
          if (pr && opt?.dataset.price) pr.textContent = opt.dataset.price;
          this.stlImage(e.target.closest('[data-stl]'), opt?.dataset.image);
        }
      });
      this.form?.addEventListener('submit', (e) => this.onSubmit(e));
      this.addEventListener('click', (e) => this.onClick(e));
      /* Find in page (or a #link) opens folded detail tabs by itself: keep the button in step */
      this.querySelector('.pdp__details[data-folded]')?.addEventListener('beforematch', () => {
        const btn = this.querySelector('[data-more-details]');
        if (btn) this.setDetailsOpen(btn, true);
      });
      this.querySelector('[data-notify-form]')?.addEventListener('submit', (e) => this.onNotify(e));
      this.setupSizeFinder();
      this.setupPanel();
      this.setupPairs();
      this.setupMaterials();

      this.guardFloaters();
      customElements.whenDefined('product-gallery').then(() => this.update(true));
      this.timer = setInterval(() => { this.renderShipToday(); this.renderDelivery(); }, 30000);
    }

    disconnectedCallback() { clearInterval(this.timer); }

    /* The chat launcher and the push bell sit bottom-right, over the right end of the add-to-bag button or the
     * "Make it yours" card while those pass through the bottom of the screen on laptops where the button opens at
     * the bottom of the first screen. Hide them meanwhile (html.pdp-buy-low, base.css). Phones keep them on screen
     * (the owner wants them always visible, Oct 2026). */
    guardFloaters() {
      const mq = matchMedia('(max-width: 989px)');
      if (mq.matches) return;
      const els = [this.querySelector('.pdp__buy'), this.querySelector('.pz-card')].filter(Boolean);
      if (!els.length) return;
      let raf = 0;
      const check = () => {
        raf = 0;
        const zone = innerHeight - 150;
        const low = els.some((el) => {
          const r = el.getBoundingClientRect();
          return r.height && r.bottom > zone && r.top < innerHeight && r.right > innerWidth - 110;
        });
        document.documentElement.classList.toggle('pdp-buy-low', low);
      };
      const queue = () => { if (!raf) raf = requestAnimationFrame(check); };
      addEventListener('scroll', queue, { passive: true });
      addEventListener('resize', queue);
      check();
    }

    /* Desktop sticky photos + buy panel: a column taller than the window scrolls with the page both ways until its
     * end shows, then sticks (theme.js T.stickyCols) */
    setupPanel() {
      window.Theme?.stickyCols?.([[this.querySelector('.pdp__media'), '--media-top', 0], [this.querySelector('.pdp__aside'), '--aside-top', 12]]);
    }

    options() { return this.picker ? this.picker.read() : this.variant.options; }

    onOptionChange(e) {
      const opts = this.options();
      const changed = Number(e.target.closest('.pdp-option')?.dataset.optionIndex ?? -1);
      const vs = this.data.variants;
      this.chosen = opts.every((v) => v !== '');
      const partial = (v) => v.options.every((o, i) => !opts[i] || o === opts[i]);
      let v = vs.find((x) => x.options.every((o, i) => o === opts[i]));
      if (!v) v = vs.find((x) => partial(x) && x.available) || vs.find(partial);
      if (!v && changed > -1) v = vs.find((x) => x.options[changed] === opts[changed] && x.available) || vs.find((x) => x.options[changed] === opts[changed]);
      this.clearFlag();
      if (v) {
        this.variant = v;
        if (this.chosen) {
          /* The chosen size doesn't exist in the new colour: another one is picked, so say which */
          const swapped = opts.map((o, i) => (i !== changed && o && o !== v.options[i] ? i : -1)).filter((i) => i > -1);
          this.picker.set(v.options);
          const note = this.querySelector('[data-choose-error]');
          if (swapped.length && note && this.s.optionSwitched) {
            const i = swapped[0];
            note.textContent = this.s.optionSwitched.replace('[previous]', opts[i]).replace('[value]', v.options[i]);
            note.hidden = false;
          }
        }
      }
      this.update(false);
    }

    update(initial) {
      const v = this.variant;
      const d = this.data;
      if (!v) return;
      this.matchPairs();
      if (this.input && this.input.value !== String(v.id)) {
        this.input.value = v.id;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (!initial && this.chosen) {
        const url = new URL(location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState(history.state, '', url);
      }
      const opts = this.options();
      this.picker?.mark(d.variants, opts, this.s.soldOutSuffix || '');

      const ci = d.colourIndex;
      const colour = ci > -1 ? v.options[ci] : null;
      const label = this.querySelector(`.pdp-option[data-option-index="${ci}"] [data-option-value]`);
      if (label && colour) label.textContent = colour;
      const named = this.querySelector('[data-selected-colour]');
      if (named && colour) named.textContent = colour;

      if (!initial) {
        /* Until every option is chosen the price stays "From …" (the lowest), as rendered */
        if (this.chosen || !d.requireChoice) this.renderPrice(v);
        const suffix = this.querySelector('[data-h1-suffix]');
        if (suffix) suffix.textContent = v.h1 ? ` ${v.h1}` : '';
      }

      const others = v.options.filter((_, i) => i !== ci && (this.chosen || opts[i]));
      const fromUrl = new URLSearchParams(location.search).has('variant');
      this.gallery?.filter?.({ colour, others, mediaId: initial && !fromUrl ? null : v.media, keepLead: initial });

      this.renderStatus();
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: v, chosen: this.chosen } }));
    }

    renderPrice(v) {
      document.querySelectorAll('[data-promo-price]:not(.promo-price--card)').forEach((n) => (n.dataset.amount = v.price));
      this.querySelectorAll('[data-club-earn]').forEach((n) => (n.dataset.cents = v.price));
      window.Theme?.clubEarn?.();
      window.Theme?.renderPromos?.();
      const wrap = this.querySelector('[data-price-wrap] .price[data-price]');
      if (!wrap) return;
      const sale = v.compare > v.price;
      wrap.classList.toggle('price--sale', sale);
      wrap.innerHTML =
        `<span class="visually-hidden">${esc(sale ? this.s.salePrice : this.s.regularPrice)}</span>` +
        `<span class="price__current" data-price-current>${esc(money(v.price))}</span>` +
        (sale ? `<span class="visually-hidden">${esc(this.s.regularPrice)}</span><s data-price-compare>${esc(money(v.compare))}</s>` : '');
    }

    missingIndex() { return this.options().findIndex((v) => v === ''); }

    buttonState() {
      const v = this.variant;
      // The Atelier early access: only members can buy until it opens to everyone (sections/product-template.liquid)
      if (this.hasAttribute('data-atelier-locked')) return { label: this.s.atelierEarly || 'Early access', disabled: true };
      if (!this.chosen) {
        const name = this.data.options[Math.max(0, this.missingIndex())] || '';
        // "Choose the amount" is already an instruction: not "Choose Choose the amount"
        const imperative = IMPERATIVE.test(name);
        return { label: imperative ? name : (this.s.choose || '').replace('[option]', name), disabled: false };
      }
      if (!v.available) return { label: this.s.soldOut, disabled: true };
      return { label: v.preorder ? this.s.preOrder : this.s.addToCart, disabled: false };
    }

    renderStatus() {
      const v = this.variant;
      const d = this.data;
      const s = this.s;
      const st = this.buttonState();
      const q = (sel) => this.querySelector(sel);

      if (this.atc) {
        this.atc.disabled = st.disabled;
        this.atc.classList.toggle('btn--gold', this.chosen && v.available && v.preorder);
        const lbl = this.atc.querySelector('[data-atc-label]');
        if (lbl) lbl.textContent = st.label;
      }
      const dyn = q('[data-dynamic-checkout]');
      if (dyn) dyn.hidden = !v.available || !this.chosen || this.hasAttribute('data-atelier-locked');

      const pre = v.available && v.preorder;
      const date = v.shipDate || d.shipDateDefault || '';
      const preBox = q('[data-preorder]');
      /* Before a choice the variant is only the page's default: say nothing about its stock or pre-order */
      if (preBox) preBox.hidden = !pre || !this.chosen;
      const dateEl = q('[data-ship-date]');
      if (dateEl) dateEl.textContent = date || s.preOrder;
      const prop = q('[data-preorder-prop]');
      if (prop) {
        /* No name unless on pre-order: Shopify's express buttons send disabled fields too */
        prop.disabled = !pre;
        if (pre) prop.name = 'properties[Preorder]'; else prop.removeAttribute('name');
        prop.value = date || s.preOrder;
      }

      const stock = q('[data-stock-line]');
      if (stock) stock.hidden = !(v.available && !v.preorder && this.chosen);
      this.renderShipToday();
      this.renderDelivery();

      const low = q('[data-low-stock]');
      if (low) {
        low.hidden = !(v.low && v.available && this.chosen);
        if (v.low) low.textContent = v.low === 1 ? s.lowStockOne : s.lowStock.replace('[count]', v.low);
      }

      /* Pre-order: "Need it sooner? Ships now in"; sold out: "Available now in". Both list the
       * colours (or variants) that share the other options and can ship now, as buttons. */
      const sooner = q('[data-ships-now]');
      if (sooner) {
        const ci = d.colourIndex;
        const same = (x) => x.id !== v.id && (ci < 0 || x.options.every((o, i) => i === ci || o === v.options[i]));
        let list = [];
        let lead = '';
        if (pre) {
          list = d.variants.filter((x) => same(x) && x.inStock);
          lead = s.shipsNowIn;
        } else if (this.chosen && !v.available) {
          list = d.variants.filter((x) => same(x) && x.available && !x.preorder);
          lead = s.availableNowIn;
        }
        const seen = new Map();
        list.forEach((x) => { const n = ci > -1 ? x.options[ci] : x.title; if (!seen.has(n)) seen.set(n, x); });
        sooner.hidden = !seen.size || !this.chosen;
        /* "Need it sooner? Ships now in:" → bold question + quiet line; each colour a chip with its leather swatch */
        const body = sooner.querySelector('[data-ships-now-body]') || sooner;
        const qm = lead.search(/[?？]/);
        const [title, rest] = qm > -1 ? [lead.slice(0, qm + 1), lead.slice(qm + 1).trim()] : ['', lead];
        const sw = (n) => {
          const tpl = sooner.querySelector('template[data-sooner-swatches]');
          const el = tpl && [...tpl.content.querySelectorAll('[data-sw]')].find((x) => x.dataset.sw === n);
          return el ? el.innerHTML : '';
        };
        body.innerHTML = seen.size
          ? `<p class="pdp-sooner__text">${title ? `<strong>${esc(title)}</strong> ` : ''}${esc(rest.replace(/[:：]\s*$/, ''))}</p>`
            + `<span class="pdp__alts">${[...seen].map(([n, x]) => `<button type="button" class="pdp__alt" data-pick-variant="${x.id}">${ci > -1 ? sw(n) : ''}<span>${esc(n)}</span><span class="pdp__alt-go" aria-hidden="true">→</span></button>`).join('')}</span>`
          : '';
      }

      const notify = q('[data-notify]');
      if (notify) {
        notify.hidden = (v.available && !v.preorder) || !this.chosen;
        const intro = q('[data-notify-intro]');
        if (intro) intro.hidden = !v.preorder;
        const f = q('[data-notify-form]');
        if (f) f.hidden = v.preorder && !this.notifyOpen;
      }

      /* Sold out: the sticky button stays enabled as "Notify me" and scrolls to the form. */
      const soldOut = this.chosen && !v.available;
      /* On phones the bar's select already asks for the option: a short button label leaves it room to show the
       * value ("MacBook Pro 16"…" instead of "Macb…") */
      const narrow = !this.chosen && this.sticky?.selects?.length && matchMedia('(max-width: 749px)').matches;
      this.sticky?.render?.({
        variant: v,
        label: soldOut ? s.notifyMe || st.label : narrow ? s.addToCart : st.label,
        disabled: soldOut ? false : st.disabled,
        showVariant: this.chosen && v.title !== 'Default Title',
        priceText: !this.chosen && d.requireChoice && d.priceVaries && s.priceFrom ? s.priceFrom.replace('[price]', money(d.priceMin)) : ''
      });
    }


    /* "Get it Tue 7 Oct – Thu 9 Oct": dispatch today when before the cutoff on a weekday (shop
     * time zone), otherwise the next weekday; then add the visitor's transit business days. */
    renderDelivery() {
      const el = this.querySelector('[data-delivery]');
      if (!el) return;
      const v = this.variant;
      const range = this.data.delivery;
      const text = el.querySelector('[data-delivery-text]');
      if (!range || !v || !v.available || v.preorder || !this.chosen || !this.s.delivery || !text) { el.hidden = true; return; }
      const tz = /([+-])(\d{2}):?(\d{2})/.exec(this.data.tz || '+0000');
      const off = tz ? (tz[1] === '-' ? -1 : 1) * (Number(tz[2]) * 60 + Number(tz[3])) : 0;
      const now = new Date(Date.now() + off * 60000);
      const DAY = 864e5;
      const weekday = (x) => (T.offDay ? !T.offDay(x, T.country) : x.getUTCDay() !== 0 && x.getUTCDay() !== 6);
      let day = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
      const cut = /^(\d{1,2}):(\d{2})/.exec(this.data.cutoff || '');
      const beforeCut = !cut || now.getUTCHours() * 60 + now.getUTCMinutes() < Number(cut[1]) * 60 + Number(cut[2]);
      if (!weekday(day) || !beforeCut) {
        do day = new Date(day.getTime() + DAY); while (!weekday(day));
      }
      const plus = (from, n) => {
        let x = from;
        let left = Math.max(0, Number(n) || 0);
        while (left > 0) { x = new Date(x.getTime() + DAY); if (weekday(x)) left -= 1; }
        return x;
      };
      const a = plus(day, range.min);
      const b = plus(day, range.max);
      let fmt;
      try {
        const loc = /^en$/i.test(T.locale || '') ? 'en-GB' : T.locale || undefined;
        fmt = new Intl.DateTimeFormat(loc, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
      } catch (e) {
        fmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
      }
      const f = (x) => fmt.format(x).replace(/,/g, '');
      const label = a.getTime() === b.getTime() ? f(a) : `${f(a)} – ${f(b)}`;
      if (el.dataset.range !== label) {
        el.dataset.range = label; /* only touch the DOM on change: the status area is a live region */
        text.innerHTML = this.s.delivery.replace('[range]', esc(label));
      }
      el.hidden = false;
    }

    renderShipToday() {
      const el = this.querySelector('[data-ship-today]');
      if (!el) return;
      const v = this.variant;
      const cut = /^(\d{1,2}):(\d{2})/.exec(this.data.cutoff || '');
      const tz = /([+-])(\d{2}):?(\d{2})/.exec(this.data.tz || '+0000');
      let text = '';
      if (cut && tz && v.available && v.inStock && !v.preorder && this.chosen) {
        const off = (tz[1] === '-' ? -1 : 1) * (Number(tz[2]) * 60 + Number(tz[3]));
        const now = new Date(Date.now() + off * 60000);
        const day = now.getUTCDay();
        const left = Number(cut[1]) * 60 + Number(cut[2]) - (now.getUTCHours() * 60 + now.getUTCMinutes());
        if (day !== 0 && day !== 6 && left > 0) text = this.s.shipToday.replace('[time]', `${Math.floor(left / 60)}h ${left % 60}m`);
      }
      el.hidden = !text;
      el.innerHTML = text ? `· ${text}` : '';
    }

    flagChoice() {
      const i = this.missingIndex();
      document.dispatchEvent(new CustomEvent('atc:blocked', { detail: { option: this.data.options[i] || '' } }));
      const group = this.querySelector(`.pdp-option[data-option-index="${i}"]`);
      const err = this.querySelector('[data-choose-error]');
      if (err) {
        const name = this.data.options[i] || '';
        // "Choose the amount" is already an instruction: shown as it is, not inside "Please choose your …"
        err.textContent = IMPERATIVE.test(name) ? name : (this.s.pleaseChoose || '').replace('[option]', name);
        err.hidden = false;
      }
      if (group) {
        group.classList.add('is-flagged');
        const r = group.getBoundingClientRect();
        if (r.top < 80 || r.bottom > innerHeight) group.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
        const select = group.querySelector('select');
        (select || group.querySelector('input'))?.focus({ preventScroll: true });
        /* "Choose a size" opens the list itself (a third of add-to-bag presses came before a choice): the Harber list
         * on desktop, the system picker where the browser allows it (Chrome/Android); iOS Safari can't, so there the
         * option stays highlighted with "Please choose…" */
        if (select && !T.selectMenu?.open(select) && typeof select.showPicker === 'function' && matchMedia('(pointer: coarse)').matches) {
          try { select.showPicker(); } catch (e) { /* not allowed here */ }
        }
      }
    }

    clearFlag() {
      this.querySelectorAll('.pdp-option.is-flagged').forEach((g) => g.classList.remove('is-flagged'));
      const err = this.querySelector('[data-choose-error]');
      if (err) err.hidden = true;
    }

    showError(msg) {
      const el = this.querySelector('[data-form-error]');
      if (!el) return;
      el.textContent = msg || '';
      el.hidden = !msg;
    }

    quantity() {
      return Math.max(1, Number(this.form?.querySelector('[name="quantity"]')?.value) || 1);
    }

    async onSubmit(e) {
      if (!this.chosen) {
        e.preventDefault();
        this.flagChoice();
        return;
      }
      if (!T.cart?.add) return; /* no JS cart: normal POST to /cart/add */
      e.preventDefault();
      if (!this.variant.available) return;
      const fd = new FormData(this.form);
      const item = { id: Number(fd.get('id')), quantity: this.quantity() };
      const props = {};
      for (const [k, val] of fd.entries()) {
        const m = /^properties\[(.+)\]$/.exec(k);
        if (m && val !== '') props[m[1]] = val;
      }
      if (Object.keys(props).length) item.properties = props;

      const lbl = this.atc?.querySelector('[data-atc-label]');
      this.showError('');
      this.atc?.setAttribute('aria-busy', 'true');
      if (lbl) lbl.textContent = this.s.adding;
      this.sticky?.busy?.(this.s.adding, true);
      try {
        await T.cart.add(item);
        if (lbl) lbl.textContent = this.s.added;
        this.sticky?.busy?.(this.s.added, false);
        setTimeout(() => this.renderStatus(), 1800);
      } catch (err) {
        this.showError(this.friendlyError(err));
        this.renderStatus();
        this.revealError();
        if (err?.data?.status === 422) this.refreshAvailability();
      } finally {
        this.atc?.removeAttribute('aria-busy');
        this.sticky?.busy?.('', false);
      }
    }

    /* Shopify's own description first; a network failure (TypeError) or unreadable response gets
     * the generic sentence instead of a browser message. */
    friendlyError(err) {
      if (err?.data?.description) return err.data.description;
      if (err instanceof TypeError || err instanceof SyntaxError) return this.s.error;
      return err?.message || this.s.error;
    }

    /* The error lives next to the main button: when that is off screen (sticky bar add), bring it in. */
    revealError() {
      const el = this.querySelector('[data-form-error]');
      if (!el || el.hidden) return;
      const r = el.getBoundingClientRect();
      if (r.top < 80 || r.bottom > innerHeight - 90) el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
    }

    /* 422 (sold out / not enough stock): re-read availability from /products/<handle>.js. */
    async refreshAvailability() {
      try {
        const r = await fetch(`${this.data.url.split('?')[0]}.js`, { headers: { Accept: 'application/json' } });
        if (!r.ok) return;
        const p = await r.json();
        (p.variants || []).forEach((fresh) => {
          const v = this.data.variants.find((x) => x.id === fresh.id);
          if (!v) return;
          v.available = !!fresh.available;
          if (!v.available) { v.inStock = false; v.preorder = false; v.low = null; }
        });
        this.picker?.mark(this.data.variants, this.options(), this.s.soldOutSuffix || '');
        this.renderStatus();
      } catch (e) { /* keep the current state */ }
    }

    stickyBuy() {
      if (!this.chosen) {
        /* Point at the missing choice right in the bar when it has selects */
        const missing = this.sticky?.selects?.find((sel) => !sel.value);
        /* Open the bar's own select where the browser can (showPicker); Safari can't, and focusing a select from
         * script doesn't open it there, so it falls through to scrolling to the option with "Please choose…" */
        if (missing && this.sticky.classList.contains('is-visible') && typeof missing.showPicker === 'function') {
          try {
            missing.focus();
            missing.showPicker();
            return;
          } catch (e) { /* not allowed here: fall through */ }
        }
        return this.flagChoice();
      }
      if (!this.variant.available) {
        const box = this.querySelector('[data-notify]');
        box?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
        box?.querySelector('input[type="email"]')?.focus({ preventScroll: true });
        return;
      }
      this.form?.requestSubmit ? this.form.requestSubmit(this.atc) : this.atc?.click();
    }

    /* Products sold with a choice of padding (the Leather Desk Mat: "WITH FELT PADDING …" / "WITH MICROFIBRE …" sizes):
     * the detail tabs' lines about the other material are hidden once a size is chosen; all show until then (owner,
     * Oct 2026). Lines are the tab's paragraphs, the "<br><br>" runs inside a paragraph and the "<span>• …</span>"
     * bullets; a line naming both materials stays. Matched in every store language. */
    setupMaterials() {
      const FELT = /\b(felt|fieltro|filz|feutre|feltro|vilt)\b/i;
      const MICRO = /micro\s?-?fib|mikrofaser|microvezel/i;
      const kindOf = (t) => { const f = FELT.test(t), m = MICRO.test(t); return f === m ? '' : f ? 'felt' : 'micro'; };
      const vals = this.data.variants.flatMap((v) => v.options || []);
      if (!vals.some((x) => kindOf(x) === 'felt') || !vals.some((x) => kindOf(x) === 'micro')) return;
      const items = [];
      const BR2 = /<br\s*\/?>\s*<br\s*\/?>/i;
      this.querySelectorAll('.pdp-acc__body .rte').forEach((rte) => {
        // A paragraph whose "<br><br>" runs name one material each becomes one paragraph per run
        rte.querySelectorAll('p').forEach((p) => {
          if (!BR2.test(p.innerHTML)) return;
          const runs = p.innerHTML.split(BR2);
          if (!runs.some((r) => kindOf(r.replace(/<[^>]+>/g, '')))) return;
          p.replaceWith(...runs.map((r) => { const x = document.createElement('p'); x.innerHTML = r; return x; }));
        });
        rte.querySelectorAll('p, li').forEach((el) => { const k = kindOf(el.textContent); if (k) items.push([k, [el]]); });
        // Bullets as spans, one per line: the span and the line break after it
        rte.querySelectorAll('p > span').forEach((el) => {
          const k = kindOf(el.textContent);
          if (!k || el.parentElement.textContent.trim() === el.textContent.trim()) return;
          const br = el.nextElementSibling?.tagName === 'BR' ? el.nextElementSibling : null;
          items.push([k, br ? [el, br] : [el]]);
        });
      });
      if (!items.length) return;
      const apply = (v, chosen) => {
        const k = chosen ? kindOf((v?.options || []).join(' ')) : '';
        items.forEach(([kind, els]) => els.forEach((el) => { el.hidden = Boolean(k) && kind !== k; }));
      };
      this.addEventListener('variant:change', (e) => apply(e.detail?.variant, e.detail?.chosen));
      apply(this.variant, this.chosen);
    }

    /* "Pairs well with": without the backend's list (harber.pairs), complementary products (Search &
     * Discovery), loaded when the panel nears the viewport. Replaces the shop-the-look fallback rows
     * only when there are results. */
    setupPairs() {
      const box = this.querySelector('[data-pairs]');
      // The backend's own list (harber.pairs) is rendered by Liquid: only the colour preselect is left to do
      if (box && !box.dataset.url && !box.hidden) this.matchPairs();
      if (!box?.dataset.url) return;
      const go = async () => {
        try {
          const r = await fetch(box.dataset.url, { headers: { Accept: 'application/json' } });
          if (!r.ok) return;
          const data = await r.json();
          const list = (data.products || []).filter((p) => p.available && p.id !== this.data.id).slice(0, 4);
          if (!list.length) return;
          const ul = box.querySelector('[data-pairs-list]');
          if (!ul) return;
          ul.innerHTML = list.map((p) => this.pairRow(p, box)).join('');
          box.hidden = false;
          this.matchPairs();
        } catch (e) { /* optional */ }
      };
      if (!('IntersectionObserver' in window)) return void go();
      const io = new IntersectionObserver((entries) => {
        if (entries.some((x) => x.isIntersecting)) { io.disconnect(); go(); }
      }, { rootMargin: '400px 0px' });
      io.observe(box.parentElement || this);
    }

    /* "Pairs well with": preselect, in each suggestion, the colour of the variant being viewed (Black →
     * Black) when that product has it. Colour names match across languages via COLOR_TRANSLATIONS. */
    matchPairs() {
      const ci = this.data.colourIndex;
      const colour = ci == null || ci < 0 ? '' : this.variant?.options?.[ci];
      if (!colour) return;
      const terms = colourTerms(colour);
      const hit = (label) => String(label || '').split('/').some((part) => terms.includes(handleize(part)));
      this.querySelectorAll('[data-stl]').forEach((row) => {
        const radios = [...row.querySelectorAll('input[data-stl-variant][type="radio"]')];
        const select = row.querySelector('select[data-stl-variant]');
        if (radios.length) {
          const r = radios.find((x) => hit(x.closest('label')?.title || x.parentElement?.textContent));
          if (!r || r.checked) return;
          r.checked = true;
          const pr = row.querySelector('.pdp-stl__price');
          if (pr && r.dataset.price) pr.textContent = r.dataset.price;
          this.stlImage(row, r.dataset.image);
        } else if (select) {
          const o = [...select.options].find((x) => hit(x.textContent));
          if (!o || o.selected) return;
          select.value = o.value;
          window.Theme?.selectFace?.refresh(select);
          const pr = row.querySelector('.pdp-stl__price');
          if (pr && o.dataset.price) pr.textContent = o.dataset.price;
          this.stlImage(row, o.dataset.image);
        }
      });
    }

    /* Swap a suggestion row's photo to the chosen variant's image (radios and selects carry data-image) */
    stlImage(row, src) {
      const im = row?.querySelector('.pdp-stl__media img');
      if (!im || !src || im.getAttribute('src') === src) return;
      im.src = src;
      im.removeAttribute('srcset');
    }

    pairRow(p, box) {
      const vs = (p.variants || []).filter((v) => v.available);
      const pv = vs[0];
      if (!pv) return '';
      const url = esc(p.url || '#');
      const title = esc(p.title);
      const sized = (src, w) => `${src}${String(src).includes('?') ? '&' : '?'}width=${w}`;
      const src = p.featured_image || p.media?.[0]?.src || '';
      const img = src ? `<img src="${esc(sized(src, 240))}" alt="" width="120" height="120" loading="lazy" decoding="async">` : '';
      const optName = p.options?.[0]?.name || p.options?.[0] || '';
      const select = vs.length > 1
        ? `<select class="pdp-stl__select" data-stl-variant aria-label="${esc((this.s.chooseOption || '').replace('[option]', optName))}">${vs.map((v) => `<option value="${v.id}" data-price="${esc(money(v.price))}"${v.featured_image?.src ? ` data-image="${esc(sized(v.featured_image.src, 240))}"` : ''}>${esc(v.title)}</option>`).join('')}</select>`
        : '';
      const plus = box.querySelector('template[data-plus]')?.innerHTML || '+';
      return `<li class="pdp-stl__item" data-stl>
        <a class="pdp-stl__media media" href="${url}" tabindex="-1" aria-hidden="true">${img}</a>
        <div class="pdp-stl__info"><a class="pdp-stl__name" href="${url}">${title}</a><span class="pdp-stl__price">${esc(money(pv.price))}</span>${select}</div>
        <button type="button" class="pdp-stl__add" data-stl-add data-variant-id="${pv.id}" aria-label="${esc((this.s.quickAdd || '').replace('[title]', p.title))}">${plus}</button>
      </li>`;
    }

    setDetailsOpen(btn, open) {
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? btn.dataset.labelClose : btn.dataset.labelOpen;
    }

    async onClick(e) {
      const t = e.target;
      const step = t.closest('[data-qty-step]');
      if (step && this.form?.contains(step)) {
        const input = this.form.querySelector('[name="quantity"]');
        if (input) input.value = Math.max(1, (Number(input.value) || 1) + Number(step.dataset.qtyStep));
        return;
      }
      const pz = t.closest('[data-personalise-open]');
      if (pz) { e.preventDefault(); return this.openPersonalise(pz); }

      const more = t.closest('[data-more-details]');
      if (more) {
        const box = this.querySelector('.pdp__details');
        if (box?.hasAttribute('data-folded')) { /* folded tabs: the button shows / hides them */
          const open = box.hasAttribute('hidden'); /* .hidden is "until-found" in Chrome */
          this.setDetailsOpen(more, open);
          Theme.slide(box, open, { hidden: 'until-found' });
          if (open) box.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest' });
          return;
        }
        const first = box?.querySelector('details.pdp-acc');
        if (first && !first.open) first.querySelector(':scope > summary')?.click(); /* animated like any accordion */
        box?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
        return;
      }

      const alt = t.closest('[data-pick-variant]');
      if (alt) {
        const v = this.data.variants.find((x) => String(x.id) === alt.dataset.pickVariant);
        if (!v) return;
        this.variant = v;
        this.chosen = true;
        this.picker?.set(v.options);
        this.clearFlag();
        this.update(false);
        return;
      }

      const nt = t.closest('[data-notify-toggle]');
      if (nt) {
        this.notifyOpen = true;
        nt.setAttribute('aria-expanded', 'true');
        const f = this.querySelector('[data-notify-form]');
        if (f) { f.hidden = false; f.querySelector('input')?.focus(); }
        return;
      }

      const pick = t.closest('[data-size-pick]');
      if (pick) {
        const best = this.data.variants.find((v) => String(v.id) === pick.dataset.sizePick);
        if (!best || !this.picker) return;
        const opts = this.options();
        best.options.forEach((o, i) => { if (i !== this.data.colourIndex) opts[i] = o; });
        this.picker.set(opts);
        this.onOptionChange({ target: this.querySelector('.pdp-option') || this.picker });
        return;
      }

      const stlRadio = t.closest?.('input[data-stl-variant][type="radio"]');
      if (stlRadio) {
        const row = stlRadio.closest('[data-stl]');
        const pr = row?.querySelector('.pdp-stl__price');
        if (pr && stlRadio.dataset.price) pr.textContent = stlRadio.dataset.price;
        const im = row?.querySelector('.pdp-stl__media img');
        if (im && stlRadio.dataset.image) { im.src = stlRadio.dataset.image; im.removeAttribute('srcset'); }
        return;
      }

      const add = t.closest('[data-stl-add]');
      if (add) {
        const row = add.closest('[data-stl]');
        const picked = row?.querySelector('input[data-stl-variant]:checked') || row?.querySelector('select[data-stl-variant]');
        const id = Number(picked?.value || add.dataset.variantId);
        const errEl = add.closest('[data-pairs]')?.querySelector('[data-stl-error]');
        if (errEl) errEl.hidden = true;
        add.setAttribute('aria-busy', 'true');
        try {
          await T.cart.add({ id, quantity: 1 });
          add.classList.add('is-done');
          setTimeout(() => add.classList.remove('is-done'), 1600);
        } catch (err) {
          if (errEl) {
            const name = row?.querySelector('.pdp-stl__name')?.textContent?.trim();
            errEl.textContent = `${name ? `${name}: ` : ''}${this.friendlyError(err)}`;
            errEl.hidden = false;
          }
        } finally {
          add.removeAttribute('aria-busy');
        }
      }
    }

    async openPersonalise(btn) {
      if (!this.chosen) return this.flagChoice();
      if (!this.variant.available) return this.showError(this.s.soldOut);
      const el = this.querySelector('product-personalise');
      if (!el) return;
      btn.setAttribute('aria-busy', 'true');
      try {
        if (!customElements.get('product-personalise')) {
          await loadScript(el.dataset.src);
          await customElements.whenDefined('product-personalise');
        }
        const v = this.variant;
        const ci = this.data.colourIndex;
        el.open({
          variant: v,
          preorder: !!v.preorder,
          quantity: this.quantity(),
          colourHandle: handleize(v.options[ci > -1 ? ci : 0]),
          image: v.image || this.data.image,
          opener: btn
        });
      } catch (err) {
        this.showError(this.s.error);
      } finally {
        btn.removeAttribute('aria-busy');
      }
    }

    async onNotify(e) {
      e.preventDefault();
      const form = e.target;
      const msg = form.querySelector('[data-notify-msg]');
      const say = (text, ok) => {
        if (!msg) return;
        msg.textContent = text;
        msg.classList.toggle('is-error', !ok);
        msg.classList.toggle('is-success', !!ok);
      };
      const email = (form.email?.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return say(this.s.notifyInvalid, false);
      const btn = form.querySelector('button[type="submit"]');
      btn?.setAttribute('aria-busy', 'true');
      // Our backend (app proxy → harberapp, same requests as the app's "Notify me"; the e-mail goes out through
      // Klaviyo's "Harber Back in Stock" flow when the variant is back). Klaviyo's own back-in-stock alert only if
      // the backend doesn't answer (the backend copies those in too).
      const backend = async () => {
        const v = this.variant || {};
        const r = await fetch('/apps/harber/back-in-stock', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ email, productId: String(this.data.id), variantId: v.id ? String(v.id) : null,
            handle: (this.data.url || '').split('/products/')[1]?.split(/[?#]/)[0] || null, title: this.data.title || null,
            variantTitle: v.title && v.title !== 'Default Title' ? v.title : null, locale: window.Theme?.locale || document.documentElement.lang || null }),
        });
        const data = await r.json().catch(() => ({}));
        // confirm: the address isn't the signed-in customer's own (or a known one), so the backend e-mailed a
        // confirmation link first (nobody can sign up someone else's inbox)
        return r.ok && data.ok ? (data.confirm ? 'confirm' : true) : false;
      };
      const klaviyo = async () => {
        const site = window.Theme?.klaviyo?.site || 'Y43BXQ';
        const r = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${site}`, {
          method: 'POST', headers: { revision: '2025-10-15', 'Content-Type': 'application/vnd.api+json' },
          body: JSON.stringify({ data: { type: 'back-in-stock-subscription', attributes: { channels: ['EMAIL'], profile: { data: { type: 'profile', attributes: { email } } } },
            relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.variant.id}` } } } } }),
        });
        return r.ok;
      };
      try {
        const ok = (await backend().catch(() => false)) || (await klaviyo().catch(() => false));
        if (!ok) throw new Error('notify');
        say(ok === 'confirm' && this.s.notifyConfirm ? this.s.notifyConfirm : this.s.notifySuccess, true);
        form.reset();
      } catch (err) {
        say(this.s.error, false);
      } finally {
        btn?.removeAttribute('aria-busy');
      }
    }

    /* "Find the right size": product metafield custom.medidas = [{sku, ancho, alto}] (cm). Same
     * matching as the previous theme: the device (L x W x H) must fit, smallest sleeve wins. */
    setupSizeFinder() {
      const root = this.querySelector('[data-size-finder]');
      if (!root) return;
      let list = this.data.medidas;
      if (typeof list === 'string') { try { list = JSON.parse(list); } catch (e) { list = null; } }
      if (!Array.isArray(list) || !list.length) { root.hidden = true; return; }
      const out = root.querySelector('[data-size-result]');
      const num = (sel) => parseFloat(String(root.querySelector(sel)?.value || '0').replace(',', '.')) || 0;
      /* The inputs sit inside the product form: Enter must run the finder, not submit (add to bag). */
      root.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' || e.isComposing || e.target.tagName !== 'INPUT') return;
        e.preventDefault();
        root.querySelector('[data-size-go]')?.click();
      });
      root.querySelector('[data-size-go]')?.addEventListener('click', () => {
        const L = num('[data-size-l]');
        const W = num('[data-size-w]');
        const H = num('[data-size-h]');
        const lengthSize = L;
        const widthSize = W + H + 0.5;
        const widthSize2 = L + H + 0.5;
        const lengthSize2 = W;
        const search = lengthSize + widthSize;
        let best = null;
        let bestScore = 9999;
        this.data.variants.forEach((v) => list.forEach((m) => {
          if (m.sku !== v.sku) return;
          const a = parseFloat(m.ancho);
          const h = parseFloat(m.alto);
          if ((a >= widthSize && h >= lengthSize) || (a >= widthSize2 && h >= lengthSize2)) {
            const sc = a + h - search;
            if (sc < bestScore) { bestScore = sc; best = v; }
          }
        }));
        if (!best) { out.innerHTML = `<p>${esc(this.s.sizeNoMatch)}</p>`; return; }
        const ci = this.data.colourIndex;
        const name = best.options.filter((_, i) => i !== ci).join(' / ') || best.title;
        out.innerHTML = `<p>${esc(this.s.sizeMatch.replace('[variant]', name))}</p>` +
          (this.picker ? `<button type="button" class="btn btn--sm btn--secondary" data-size-pick="${best.id}">${esc(this.s.sizeSelect)}</button>` : '');
      });
    }
  }
  define('product-info', ProductInfo);

  /* ---------------------------------------------- Product recommendations */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      /* The page renders skeleton cards only. Shopify's related recommendations (data-url) replace
       * the block when they have cards; otherwise the fallback view (data-fallback: shop the look,
       * closest collection, bestsellers) fills the list. Nothing at all → the block hides. */
      if (this.loaded || !this.dataset.url) return;
      const go = () => { if (!this.loaded) { this.loaded = true; this.load(); } };
      if (!('IntersectionObserver' in window)) return go();
      new IntersectionObserver((entries, o) => {
        if (entries.some((x) => x.isIntersecting)) { o.disconnect(); go(); }
      }, { rootMargin: '600px 0px' }).observe(this);
    }

    async load() {
      try {
        const r = await fetch(this.dataset.url);
        if (r.ok) {
          const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
          const fresh = doc.querySelector('product-recommendations');
          if (fresh && fresh.querySelector('product-card')) {
            this.innerHTML = fresh.innerHTML;
            T.reveal?.(this);
            return;
          }
        }
        if (await this.fallback()) return;
      } catch (e) { /* recommendations are optional */ }
      if (this.querySelector('.product-recs__skel')) this.hidden = true;
    }

    async fallback() {
      if (!this.dataset.fallback) return false;
      const r = await fetch(this.dataset.fallback);
      if (!r.ok) return false;
      const html = (await r.text()).trim();
      if (!html.includes('product-card')) return false;
      /* Rebuild the block so <scroll-carousel> initialises with the real cards */
      const tpl = document.createElement('template');
      tpl.innerHTML = this.innerHTML;
      const list = tpl.content.querySelector('[data-track]');
      if (!list) return false;
      list.innerHTML = html;
      list.removeAttribute('aria-busy');
      this.innerHTML = '';
      this.append(tpl.content);
      T.reveal?.(this);
      return true;
    }
  }
  define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------- Reviews: write CTA */
  document.addEventListener('click', (e) => {
    const cta = e.target.closest('[data-review-cta]');
    if (!cta) return;
    const link = document.querySelector('.jdgm-write-rev-link, .jdgm-rev-widg__write-rev-link');
    if (link) { e.preventDefault(); link.click(); return; }
    const widget = document.querySelector('.jdgm-review-widget');
    if (widget) { e.preventDefault(); widget.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' }); }
  });

  /* ------------------------------------------- "Not sure your device?" size helper
   * Opens in a drawer on tech products: the shopper types the model number (e.g. A2338), the
   * device list is fetched once (/pages/find-your-device?view=device-data), and the matching size
   * of this product is selected in the option it was opened from. */
  document.querySelectorAll('[data-device-fit]').forEach((root) => {
    const form = root.querySelector('[data-fit-form]');
    const out = root.querySelector('[data-fit-result]');
    let S = {};
    try { S = JSON.parse(root.querySelector('template[data-fit-strings]').innerHTML); } catch (e) { /* defaults below */ }
    /* `t` HTML-escapes the strings (isn&#39;t): decode them once for textContent */
    const decode = (v) => { const ta = document.createElement('textarea'); ta.innerHTML = v; return ta.value; };
    Object.keys(S).forEach((k) => { if (k !== 'chooseFromList') S[k] = decode(S[k]); });
    const norm = (v) => String(v || '').toUpperCase().replace(/\s+/g, '');
    const key = (v) => String(v || '').toLowerCase().normalize('NFD').replace(/[^a-z0-9]/g, '');
    const para = (text, cls, html) => {
      const el = document.createElement('p');
      if (cls) el.className = cls;
      if (html) el.innerHTML = text; else el.textContent = text;
      return el;
    };
    let group = null;
    let devices = null;

    document.querySelectorAll(`[aria-controls="${root.id}"]`).forEach((a) => a.addEventListener('click', () => {
      group = a.closest('.pdp-option');
      setTimeout(() => form.elements.model.focus(), 60);
    }));

    const load = () => {
      devices = devices || fetch(root.dataset.url)
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((t) => JSON.parse(t))
        .catch((err) => { devices = null; throw err; });
      return devices;
    };

    /* The option value whose text names the device ("MacBook Pro 13\" (2016 / M2-2022)") */
    const values = (g) => {
      if (!g) return [];
      const sel = g.querySelector('[data-option-select]');
      if (sel) return [...sel.options].filter((o) => o.value).map((o) => o.value);
      return [...g.querySelectorAll('[data-option-radio]')].map((r) => r.value);
    };
    /* Device name → this product's option value. Exact or contained names first; then, only when exactly one value
     * qualifies (never a guess): the same name with other years in brackets ("iPad Mini (2021 / 2024)" →
     * "iPad Mini (2012 / 2024)", overlapping years decide between several), a laptop band ('13"-14" inch Laptops'),
     * phone sizes ("SmartPhone" / "SmartPhone Plus" for Plus / Pro Max) and a value naming several models of the same
     * size ("iPad Pro / Air 13""). Keyboard versions only for keyboard devices. */
    const info = (s) => {
      const t = String(s || '').toLowerCase().replace(/\s+-\s+new\b/, '');
      const paren = (t.match(/\(([^)]*)\)/g) || []).join(' ');
      const bare = t.replace(/\([^)]*\)/g, ' ');
      return {
        base: key(bare.replace(/with magic keyboard|\+ keyboard/g, '')),
        keyboard: /keyboard/.test(t),
        family: (/(macbook|ipad|iphone|kindle|dell|surface|smartphone)/.exec(t) || [])[1] || '',
        inches: (bare.match(/\d+(?:\.\d+)?(?=\s*(?:"|”|''|inch))/g) || []).map(Number),
        words: new Set(bare.match(/\b(air|pro|mini|max|plus|se)\b/g) || []),
        years: (paren.match(/(?:19|20)\d\d/g) || []).map(Number)
      };
    };
    const findValue = (g, name) => {
      const k = key(name);
      const list = values(g);
      const direct = list.find((v) => key(v) === k) || list.find((v) => key(v).includes(k) || k.includes(key(v)));
      if (direct) return direct;
      const d = info(name);
      const vs = list.map((v) => ({ v, i: info(v) }));
      const one = (arr) => (arr.length === 1 ? arr[0].v : null);
      const same = vs.filter((x) => x.i.base === d.base && x.i.keyboard === d.keyboard);
      if (same.length === 1) return same[0].v;
      if (same.length > 1 && d.years.length) {
        const lo = Math.min(...d.years);
        const hi = Math.max(...d.years);
        const hit = one(same.filter((x) => x.i.years.length && Math.min(...x.i.years) <= hi && Math.max(...x.i.years) >= lo));
        if (hit) return hit;
      }
      if (d.inches.length && /(macbook|dell|surface)/.test(d.family)) {
        const hit = one(vs.filter((x) => /laptop/i.test(x.v) && x.i.inches.length === 2 && d.inches[0] >= Math.min(...x.i.inches) - 0.01 && d.inches[0] < Math.max(...x.i.inches) + 1));
        if (hit) return hit;
      }
      if (d.family === 'iphone' && vs.some((x) => x.i.family === 'smartphone')) {
        const big = d.words.has('max') || d.words.has('plus');
        const hit = one(vs.filter((x) => x.i.family === 'smartphone' && x.i.words.has('plus') === big && !x.i.words.has('max')));
        if (hit) return hit;
      }
      if (d.family && d.inches.length) {
        const hit = one(vs.filter((x) => x.i.family === d.family && !x.i.keyboard && x.i.inches.length === 1 && x.i.inches[0] === d.inches[0] && x.i.words.size > 1 && [...d.words].every((w) => x.i.words.has(w))));
        if (hit) return hit;
      }
      return undefined;
    };
    const choose = (g, value) => {
      const sel = g.querySelector('[data-option-select]');
      let input = sel;
      if (sel) sel.value = value;
      else {
        input = [...g.querySelectorAll('[data-option-radio]')].find((r) => r.value === value);
        if (input) input.checked = true;
      }
      input?.dispatchEvent(new Event('change', { bubbles: true }));
    };

    /* The device remembered by the home finder band (localStorage 'harber:device'): mark the size that
     * fits it and, unless a variant was chosen in the URL, select it. A match in this drawer is
     * remembered too. */
    const fitGroup = () => [...(root.closest('product-info')?.querySelectorAll('.pdp-option') || document.querySelectorAll('.pdp-option'))].find((o) => o.querySelector('[data-device-fit-open]'));
    const remember = (device) => {
      try {
        const family = /^(macbook|ipad|iphone)/.exec(key(device.name))?.[1] || null;
        localStorage.setItem('harber:device', JSON.stringify({ name: device.name, models: device.models || [], handles: device.handles || [], family }));
      } catch (err) { /* private mode */ }
    };
    /* While another size is selected (a ?variant= link, or the shopper changed it) the note doesn't claim a fit: it
     * says which size the device needs, with a button to choose it. */
    (() => {
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem('harber:device') || 'null'); } catch (err) { return; }
      if (!saved?.name || !(saved.handles || []).map((h) => String(h).trim()).includes(root.dataset.handle)) return;
      const g = fitGroup();
      const value = g && findValue(g, saved.name);
      if (!value) return;
      const note = para('', 'pdp-fit__saved');
      (g.querySelector('.pdp-option__values, .pdp-select') || g.lastElementChild).after(note);
      const current = () => g.querySelector('[data-option-select]')?.value ?? g.querySelector('[data-option-radio]:checked')?.value ?? '';
      const paint = () => {
        const sel = current();
        note.classList.toggle('is-other', !!sel && sel !== value);
        if (!sel || sel === value) { note.textContent = (S.fitsSaved || '').replace('%DEVICE%', saved.name); return; }
        note.textContent = `${(S.savedOther || '').replace('%DEVICE%', saved.name).replace('%SIZE%', value.replace(/\s+-\s+new$/i, ''))} `;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pdp-fit__choose';
        b.textContent = S.selectSize || '';
        b.addEventListener('click', () => choose(g, value));
        note.append(b);
      };
      if (!new URL(location.href).searchParams.has('variant')) choose(g, value);
      paint();
      g.addEventListener('change', () => setTimeout(paint, 0));
    })();

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const model = norm(form.elements.model.value);
      if (!model) return;
      out.className = 'pdp-fit__result';
      out.replaceChildren(para(S.loading || '…'));
      let list;
      try { list = await load(); } catch (err) { out.replaceChildren(para(S.doubleCheck || '')); return; }
      const device = list.find((d) => (d.models || []).some((m) => norm(m) === model || norm(m) === `A${model}`));
      out.replaceChildren();
      if (!device) {
        out.classList.add('pdp-fit__result--miss');
        out.append(para(S.noMatch || ''), para(S.doubleCheck || ''));
        return;
      }
      out.append(para(S.yourDevice || ''), para(device.name, 'pdp-fit__device'));
      const fits = (device.handles || []).map((h) => String(h).trim()).includes(root.dataset.handle);
      if (!fits) {
        out.classList.add('pdp-fit__result--miss');
        const a = document.createElement('a');
        a.className = 'btn btn--secondary';
        a.href = `${root.dataset.finder}?model=${encodeURIComponent(model)}`;
        a.textContent = S.seeFitting || '';
        out.append(para(S.doesntFit || ''), a);
        return;
      }
      remember(device);
      const g = group || fitGroup();
      const value = findValue(g, device.name);
      if (!value) { out.append(para(S.chooseFromList || '', null, true)); return; }
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn';
      btn.textContent = `${S.selectSize || ''}: ${value}`;
      btn.addEventListener('click', () => {
        choose(g, value);
        root.close?.();
      });
      out.append(btn);
    });
  });

  /* Shipping & returns drawer: the merchant text lists duties per region as "A. UK … B. EU … C. US …
   * D. Other countries". Put the visitor's own region first and mark it, in any language (the letters
   * don't change between translations). The region comes from the market the visitor is on; when that is
   * still the store's default market, Shopify's IP geolocation (browsing_context_suggestions) is used. */
  (() => {
    const bodies = [...document.querySelectorAll('modal-dialog[id^="PdpShipping-"] .pdp-drawer__body')];
    const market = String(T.country || '').toUpperCase();
    if (!bodies.length || !market) return;
    const EU = 'AT,BE,BG,HR,CY,CZ,DK,EE,FI,FR,DE,GR,HU,IE,IT,LV,LT,LU,MT,NL,PL,PT,RO,SK,SI,ES,SE'.split(',');
    /* Merchant text often keeps A–D in one block separated by blank lines (<br><br>): split such blocks
     * into one element per paragraph first, so each region can move on its own. */
    const startsWithLetter = (html) => /^\s*[A-D][.)]\s/.test(html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' '));
    bodies.forEach((body) => {
      body.querySelectorAll('p, div').forEach((block) => {
        if (block.children.length && [...block.children].some((c) => /^(P|DIV|UL|OL)$/.test(c.tagName))) return;
        const parts = block.innerHTML.split(/(?:<br\s*\/?>\s*){2,}/i);
        if (parts.filter(startsWithLetter).length < 2) return;
        const wrap = document.createElement('div');
        wrap.className = block.className;
        parts.forEach((html) => {
          if (!html.replace(/<[^>]*>/g, '').trim()) return;
          const para = document.createElement('p');
          para.innerHTML = html.trim();
          wrap.append(para);
        });
        block.replaceWith(wrap);
      });
    });
    /* The first text node that starts with the region letter (it can sit inside <strong> or follow an
     * empty tag), so the letter can be renumbered without touching the rest of the markup. */
    const letterNode = (el) => {
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        if (!n.nodeValue.trim()) continue;
        return /^\s*[A-D]/.test(n.nodeValue) ? n : null;
      }
      return null;
    };
    const apply = (country) => {
      const letter = country === 'GB' ? 'A' : EU.includes(country) ? 'B' : country === 'US' ? 'C' : 'D';
      bodies.forEach((body) => {
        const items = [...body.querySelectorAll('p, li')].filter((el) => el.dataset.region || /^[A-D][.)]\s/.test(el.textContent.trim()));
        if (items.length < 2) return;
        /* remember each paragraph's original region letter once */
        items.forEach((el) => { if (!el.dataset.region) el.dataset.region = el.textContent.trim()[0]; });
        const order = ['A', 'B', 'C', 'D'];
        const anchor = items[0].parentNode;
        const first = items.reduce((f, el) => (el.compareDocumentPosition(f) & Node.DOCUMENT_POSITION_FOLLOWING ? el : f), items[0]);
        const marker = document.createComment('');
        anchor.insertBefore(marker, first);
        const sorted = items.slice().sort((x, y) => order.indexOf(x.dataset.region) - order.indexOf(y.dataset.region));
        const mine = sorted.find((el) => el.dataset.region === letter);
        [mine, ...sorted.filter((el) => el !== mine)].filter(Boolean).forEach((el, i) => {
          el.classList.toggle('pdp-ship__mine', el === mine);
          anchor.insertBefore(el, marker);
          /* renumber in the new order: the visitor's region becomes A */
          const node = letterNode(el);
          if (node) node.nodeValue = node.nodeValue.replace(/^(\s*)[A-D]/, `$1${order[i]}`);
        });
        marker.remove();
      });
    };
    apply(market);
    if (market !== String(T.shopCountry || 'GB').toUpperCase()) return;
    /* Default market: ask Shopify where the visitor actually is (cached for the session) */
    let cached = null;
    try { cached = sessionStorage.getItem('harber:geo'); } catch (e) { /* storage blocked */ }
    if (cached) { if (cached !== market) apply(cached); return; }
    fetch('/browsing_context_suggestions.json?country[enabled]=true')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const geo = String(d?.detected_values?.country?.handle || '').toUpperCase();
        if (!geo) return;
        try { sessionStorage.setItem('harber:geo', geo); } catch (e) { /* storage blocked */ }
        if (geo !== market) apply(geo);
      })
      .catch(() => {});
  })();
})();
